(() => {
  const ELEMENT_NAME = 'cws-home-header';

  if (customElements.get(ELEMENT_NAME)) return;

  class CwsHomeHeader extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.breakpoint = Number(this.dataset.mobileBreakpoint) || 1000;
      this.menuToggle = this.querySelector('[data-cws-menu-toggle]');
      this.navigation = this.querySelector('[data-cws-navigation]');
      this.navigationInner = this.navigation?.querySelector('.cws-home-header__navigation-inner');
      this.searchToggle = this.querySelector('[data-cws-search-toggle]');
      this.searchForm = this.querySelector('[data-cws-search]');

      this.menuToggle?.addEventListener('click', () => this.toggleNavigation(), { signal: this.signal });
      this.searchToggle?.addEventListener('click', () => this.toggleSearch(), { signal: this.signal });
      this.addEventListener('click', (event) => this.onHeaderClick(event), { signal: this.signal });
      this.navigation?.addEventListener('click', (event) => this.onNavigationClick(event), { signal: this.signal });
      this.navigation?.addEventListener('keydown', (event) => this.onNavigationKeydown(event), { signal: this.signal });
      document.addEventListener('keydown', (event) => this.onDocumentKeydown(event), { signal: this.signal });
      document.addEventListener('click', (event) => this.onDocumentClick(event), { signal: this.signal });
      window.addEventListener('resize', () => this.onResize(), { signal: this.signal, passive: true });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      document.body.classList.remove('cws-header-navigation-open');
    }

    isMobile() {
      return window.innerWidth <= this.breakpoint;
    }

    isNavigationOpen() {
      return this.navigation?.classList.contains('is-open') || false;
    }

    toggleNavigation(force) {
      if (!this.navigation || !this.menuToggle) return;

      const shouldOpen = typeof force === 'boolean' ? force : !this.isNavigationOpen();
      if (shouldOpen) this.toggleSearch(false);
      this.navigation.classList.toggle('is-open', shouldOpen);
      this.menuToggle.setAttribute('aria-expanded', String(shouldOpen));
      this.menuToggle.setAttribute(
        'aria-label',
        shouldOpen ? this.dataset.menuCloseLabel : this.dataset.menuOpenLabel
      );
      document.body.classList.toggle('cws-header-navigation-open', shouldOpen);

      if (shouldOpen) {
        this.updateNavigationHeight();
      } else {
        this.closeSubmenus();
        this.navigationInner?.style.removeProperty('max-height');
      }
    }

    toggleSearch(force) {
      if (!this.searchForm || !this.searchToggle) return;

      const isOpen = this.searchForm.classList.contains('is-open');
      const shouldOpen = typeof force === 'boolean' ? force : !isOpen;
      if (shouldOpen && this.isNavigationOpen()) this.toggleNavigation(false);
      this.searchForm.classList.toggle('is-open', shouldOpen);
      this.searchToggle.setAttribute('aria-expanded', String(shouldOpen));

      if (shouldOpen) {
        requestAnimationFrame(() => this.searchForm.querySelector('input')?.focus({ preventScroll: true }));
      }
    }

    onNavigationClick(event) {
      const tab = event.target.closest('[data-cws-tab]');
      if (tab) {
        event.preventDefault();
        this.activateTab(tab);
        return;
      }

      const submenuToggle = event.target.closest('[data-cws-submenu-toggle]');
      if (submenuToggle && this.isMobile()) {
        event.preventDefault();
        this.toggleSubmenu(submenuToggle);
        return;
      }

      const link = event.target.closest('a[href]');
      if (link && this.isNavigationOpen()) this.toggleNavigation(false);
    }

    onHeaderClick(event) {
      if (!this.isNavigationOpen()) return;
      if (event.target.closest('.cws-home-header__actions a[href]')) this.toggleNavigation(false);
    }

    onNavigationKeydown(event) {
      const tab = event.target.closest('[data-cws-tab]');
      if (tab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        this.moveTabFocus(tab, event.key);
        return;
      }

      const submenuToggle = event.target.closest('[data-cws-submenu-toggle]');
      if (submenuToggle && this.isMobile() && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        this.toggleSubmenu(submenuToggle);
      }
    }

    onDocumentKeydown(event) {
      if (event.key !== 'Escape') return;

      if (this.isNavigationOpen()) {
        event.preventDefault();
        this.toggleNavigation(false);
        this.menuToggle?.focus({ preventScroll: true });
        return;
      }

      if (this.searchForm?.classList.contains('is-open')) {
        event.preventDefault();
        this.toggleSearch(false);
        this.searchToggle?.focus({ preventScroll: true });
      }
    }

    onDocumentClick(event) {
      if (!this.isNavigationOpen() || this.contains(event.target)) return;
      this.toggleNavigation(false);
    }

    onResize() {
      if (!this.isMobile()) {
        if (this.isNavigationOpen()) this.toggleNavigation(false);
        this.toggleSearch(false);
        this.closeSubmenus();
        return;
      }

      if (this.isNavigationOpen()) this.updateNavigationHeight();
    }

    toggleSubmenu(toggle) {
      const item = toggle.closest('[data-cws-navigation-item]');
      if (!item) return;

      const shouldOpen = !item.classList.contains('is-open');
      this.querySelectorAll('[data-cws-navigation-item].is-open').forEach((openItem) => {
        if (openItem !== item) {
          openItem.classList.remove('is-open');
          openItem.querySelector('[data-cws-submenu-toggle]')?.setAttribute('aria-expanded', 'false');
        }
      });

      item.classList.toggle('is-open', shouldOpen);
      toggle.setAttribute('aria-expanded', String(shouldOpen));
      requestAnimationFrame(() => this.updateNavigationHeight());
    }

    closeSubmenus() {
      this.querySelectorAll('[data-cws-navigation-item].is-open').forEach((item) => {
        item.classList.remove('is-open');
      });
      this.querySelectorAll('[data-cws-submenu-toggle][aria-expanded="true"]').forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
      });
    }

    updateNavigationHeight() {
      if (!this.navigationInner) return;
      const top = this.navigation.getBoundingClientRect().top;
      const availableHeight = Math.max(240, window.innerHeight - top);
      this.navigationInner.style.maxHeight = `${availableHeight}px`;
    }

    activateTab(tab) {
      const tabs = [...tab.closest('[role="tablist"]').querySelectorAll('[data-cws-tab]')];
      const panel = this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);

      tabs.forEach((candidate) => {
        const isActive = candidate === tab;
        candidate.classList.toggle('is-active', isActive);
        candidate.setAttribute('aria-selected', String(isActive));
        candidate.tabIndex = isActive ? 0 : -1;

        const candidatePanel = this.querySelector(`#${CSS.escape(candidate.getAttribute('aria-controls'))}`);
        if (candidatePanel) candidatePanel.hidden = !isActive;
      });

      panel?.removeAttribute('hidden');
      requestAnimationFrame(() => this.updateNavigationHeight());
    }

    moveTabFocus(tab, key) {
      const tabs = [...tab.closest('[role="tablist"]').querySelectorAll('[data-cws-tab]')];
      const currentIndex = tabs.indexOf(tab);
      let nextIndex = currentIndex;

      if (key === 'Home') nextIndex = 0;
      if (key === 'End') nextIndex = tabs.length - 1;
      if (key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
      if (key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;

      this.activateTab(tabs[nextIndex]);
      tabs[nextIndex].focus();
    }
  }

  customElements.define(ELEMENT_NAME, CwsHomeHeader);
})();
